import { useState } from "react";
import { History, Sparkles, CheckCircle2, CircleOff, BellRing } from "lucide-react";
import { fmtUsd } from "@/lib/api";
import { Hint } from "./Hint";

const STYLE = {
  created: { Icon: Sparkles, dot: "bg-cyan-400", ring: "ring-cyan-500/30", text: "text-cyan-200" },
  met: { Icon: CheckCircle2, dot: "bg-emerald-400", ring: "ring-emerald-500/30", text: "text-emerald-200" },
  unmet: { Icon: CircleOff, dot: "bg-amber-400", ring: "ring-amber-500/30", text: "text-amber-200" },
  alert_fired: { Icon: BellRing, dot: "bg-violet-400", ring: "ring-violet-500/30", text: "text-violet-200" },
};

const relTime = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const fmtVal = (e, v) => {
  if (v === null || v === undefined) return "—";
  if (/Close|SMA|Bollinger|support|resistance/i.test(`${e.metric} ${e.label}`) && Math.abs(v) >= 0.01) return fmtUsd(v);
  return Number(v).toFixed(Math.abs(v) < 1 ? 4 : 2);
};

const title = (e) => {
  if (e.type === "created") return `Analysis generated · ${e.met_count}/${e.total} conditions met`;
  if (e.type === "alert_fired") return `Alert fired · ${e.met_count}/${e.total} conditions met`;
  return `${e.type === "met" ? "Met" : "No longer met"}: ${e.label}`;
};

export const SetupTimeline = ({ events = [], createdAt }) => {
  const [showAll, setShowAll] = useState(false);
  const list = events.length ? events : createdAt ? [{ id: "created", type: "created", at: createdAt, met_count: "—", total: "—" }] : [];
  if (!list.length) return null;
  const ordered = [...list].reverse();
  const visible = showAll ? ordered : ordered.slice(0, 5);
  const flips = events.filter((e) => e.type === "met" || e.type === "unmet").length;

  return (
    <div className="mt-4 pt-3 border-t border-slate-800/70" data-testid="setup-timeline">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-cyan-300" />
        <Hint k="timeline" side="left"><h4 className="font-display text-sm font-semibold cursor-help">Setup Change Timeline</h4></Hint>
        <span className="ml-auto text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400" data-testid="timeline-flip-count">
          {flips} change{flips === 1 ? "" : "s"}
        </span>
      </div>
      <ol className="relative ml-2 border-l border-slate-800 space-y-3" data-testid="timeline-list">
        {visible.map((e, i) => {
          const s = STYLE[e.type] || STYLE.unmet;
          return (
            <li key={e.id || i} className="pl-4 relative" data-testid={`timeline-event-${e.type}`} style={{ animation: `rise .35s ease ${i * 40}ms both` }}>
              <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ${s.dot} ring-4 ${s.ring}`} />
              <div className={`text-xs font-medium leading-snug ${s.text}`}>{title(e)}</div>
              <div className="flex gap-3 flex-wrap mt-0.5 text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">
                <span title={new Date(e.at).toLocaleString()}>{relTime(e.at)}</span>
                {e.type !== "created" && e.type !== "alert_fired" && (
                  <span>Value: <span className="text-slate-300">{fmtVal(e, e.current)}</span> vs {e.operator} {fmtVal(e, e.target)}</span>
                )}
                {e.price != null && <span>Price {fmtUsd(e.price)}</span>}
              </div>
            </li>
          );
        })}
      </ol>
      {ordered.length > 5 && (
        <button data-testid="timeline-toggle" onClick={() => setShowAll((v) => !v)} className="btn-ghost rounded-md px-2 py-1 text-[11px] mt-2">
          {showAll ? "Show fewer" : `Show all ${ordered.length} events`}
        </button>
      )}
      {flips === 0 && (
        <div className="text-[10px] text-slate-500 italic mt-2" data-testid="timeline-empty-hint">
          No condition has flipped yet. Changes are recorded on every re-check (manual or the 15-minute background sweep).
        </div>
      )}
    </div>
  );
};
